import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
    ChevronDown, Package, CheckSquare, Square, ArrowRight
} from "lucide-react";
import { getIcon } from "../../utils/icons";

export function SubcategoryPills({ parent, children, currentSlug }) {
    if (!children || children.length === 0) return null;
    const allCount = children.reduce((s, c) => s + (c.product_count || 0), 0);

    return (
        <div className="mb-6" data-testid="subcategory-panel">
            <div className="flex flex-wrap gap-2.5">
                <Link to={`/kategorie/${parent.slug}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all
                        ${currentSlug === parent.slug || !currentSlug
                            ? "bg-green-600 text-white shadow-sm shadow-green-200"
                            : "bg-white text-gray-600 border border-gray-200 hover:border-green-300 hover:text-green-700"}`}
                    data-testid="subcat-all">
                    <Package size={13} />
                    <span>Vše</span>
                    <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold
                        ${currentSlug === parent.slug ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>
                        {allCount}
                    </span>
                </Link>
                {children.map(ch => {
                    const Icon = getIcon(ch.icon);
                    const isActive = currentSlug === ch.slug;
                    return (
                        <Link key={ch.id} to={`/kategorie/${ch.slug}`}
                            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all
                                ${isActive ? "bg-green-600 text-white shadow-sm shadow-green-200"
                                    : "bg-white text-gray-600 border border-gray-200 hover:border-green-300 hover:text-green-700"}`}
                            data-testid={`subcat-${ch.slug}`}>
                            <Icon size={13} className={isActive ? "text-white" : "text-gray-400"} />
                            <span>{ch.name}</span>
                            {ch.product_count > 0 && (
                                <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold
                                    ${isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>
                                    {ch.product_count}
                                </span>
                            )}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}

export function SubcategoryGrid({ parent, children, currentSlug }) {
    if (!children || children.length === 0) return null;

    return (
        <div className="mb-6" data-testid="subcategory-panel">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2">
                <Link to={`/kategorie/${parent.slug}`}
                    className={`group relative flex flex-col items-center justify-center rounded-xl p-3 transition-all duration-200 text-center min-h-[88px] ${
                        currentSlug === parent.slug
                            ? "bg-green-600 text-white shadow-md shadow-green-200"
                            : "bg-white border border-gray-100 hover:border-green-300 hover:shadow-md hover:shadow-green-100/40"
                    }`}
                    data-testid="subcat-all">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-1.5 ${
                        currentSlug === parent.slug ? "bg-white/20" : "bg-green-50 group-hover:bg-green-100"
                    } transition-colors`}>
                        <Package size={16} className={currentSlug === parent.slug ? "text-white" : "text-green-600"} />
                    </div>
                    <span className={`text-[11px] font-bold leading-tight ${currentSlug === parent.slug ? "text-white" : "text-gray-700"}`}>Vše</span>
                    <span className={`text-[10px] font-semibold mt-0.5 ${currentSlug === parent.slug ? "text-white/70" : "text-gray-400"}`}>
                        {children.reduce((s, c) => s + (c.product_count || 0), 0)}
                    </span>
                </Link>
                {children.map(ch => {
                    const Icon = getIcon(ch.icon);
                    const isActive = currentSlug === ch.slug;
                    return (
                        <Link key={ch.id} to={`/kategorie/${ch.slug}`}
                            className={`group relative flex flex-col items-center justify-center rounded-xl p-3 transition-all duration-200 text-center min-h-[88px] ${
                                isActive
                                    ? "bg-green-600 text-white shadow-md shadow-green-200"
                                    : "bg-white border border-gray-100 hover:border-green-300 hover:shadow-md hover:shadow-green-100/40"
                            }`}
                            data-testid={`subcat-${ch.slug}`}>
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-1.5 ${
                                isActive ? "bg-white/20" : "bg-gray-50 group-hover:bg-green-50"
                            } transition-colors`}>
                                <Icon size={16} className={isActive ? "text-white" : "text-gray-500 group-hover:text-green-600"} />
                            </div>
                            <span className={`text-[11px] font-bold leading-tight line-clamp-2 ${isActive ? "text-white" : "text-gray-700 group-hover:text-green-700"}`}>
                                {ch.name}
                            </span>
                            {ch.product_count > 0 && (
                                <span className={`text-[10px] font-semibold mt-0.5 ${isActive ? "text-white/70" : "text-gray-400"}`}>
                                    {ch.product_count}
                                </span>
                            )}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}

export function SubcategoryTable({ parent, children, currentSlug }) {
    if (!children || children.length === 0) return null;
    const allCount = children.reduce((s, c) => s + (c.product_count || 0), 0);

    return (
        <div className="mb-6 bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid="subcategory-panel">
            <div className="divide-y divide-gray-50">
                <Link to={`/kategorie/${parent.slug}`}
                    className={`flex items-center gap-3 px-4 py-2 transition-colors group ${
                        currentSlug === parent.slug ? "bg-green-50" : "hover:bg-gray-50"
                    }`}
                    data-testid="subcat-all">
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${
                        currentSlug === parent.slug ? "bg-green-600 text-white" : "bg-green-50 text-green-600"
                    }`}>
                        <Package size={12} />
                    </div>
                    <span className={`text-sm font-bold flex-1 ${currentSlug === parent.slug ? "text-green-700" : "text-gray-800"}`}>Všechny produkty</span>
                    <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{allCount}</span>
                    <ArrowRight size={11} className="text-gray-300 group-hover:text-green-500 transition-colors" />
                </Link>
                {children.map(ch => {
                    const Icon = getIcon(ch.icon);
                    const isActive = currentSlug === ch.slug;
                    return (
                        <Link key={ch.id} to={`/kategorie/${ch.slug}`}
                            className={`flex items-center gap-3 px-4 py-2 transition-colors group ${
                                isActive ? "bg-green-50" : "hover:bg-gray-50"
                            }`}
                            data-testid={`subcat-${ch.slug}`}>
                            <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${
                                isActive ? "bg-green-600 text-white" : "bg-gray-50 text-gray-500 group-hover:bg-green-50 group-hover:text-green-600"
                            } transition-colors`}>
                                <Icon size={12} />
                            </div>
                            <span className={`text-sm font-medium flex-1 ${isActive ? "text-green-700 font-bold" : "text-gray-700 group-hover:text-green-700"}`}>
                                {ch.name}
                            </span>
                            {ch.product_count > 0 && (
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                    isActive ? "bg-green-600 text-white" : "bg-gray-100 text-gray-400"
                                }`}>{ch.product_count}</span>
                            )}
                            <ArrowRight size={11} className="text-gray-300 group-hover:text-green-500 transition-colors" />
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}

export function SubcategoryPanel({ parent, children, currentSlug, displayStyle }) {
    if (!children || children.length === 0) return null;
    if (displayStyle === "grid") return <SubcategoryGrid parent={parent} children={children} currentSlug={currentSlug} />;
    if (displayStyle === "table") return <SubcategoryTable parent={parent} children={children} currentSlug={currentSlug} />;
    return <SubcategoryPills parent={parent} children={children} currentSlug={currentSlug} />;
}

export function FilterDropdown({ label, active, count, children, className = "" }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", fn);
        return () => document.removeEventListener("mousedown", fn);
    }, []);

    return (
        <div className={`relative ${className}`} ref={ref}>
            <button type="button"
                onClick={() => setOpen(o => !o)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    active ? "bg-green-50 text-green-700 border border-green-300" : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
                } ${open ? "ring-2 ring-green-200" : ""}`}
                data-testid={`filter-btn-${label}`}>
                {label}
                {count > 0 && (
                    <span className="bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{count}</span>
                )}
                <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl z-[200] min-w-[220px]"
                    style={{ boxShadow: "0 12px 40px rgba(0,0,0,0.12)" }}>
                    <div className="p-1">{children(setOpen)}</div>
                </div>
            )}
        </div>
    );
}

export function CategoryGrid({ categories }) {
    const topLevel = categories.filter(c => !c.parent_id && c.is_active !== false);
    if (topLevel.length === 0) return null;

    return (
        <div className="mb-8" data-testid="category-grid">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Vyberte kategorii</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {topLevel.map(cat => {
                    const Icon = getIcon(cat.icon);
                    return (
                        <Link key={cat.id} to={`/kategorie/${cat.slug}`}
                            className="group relative bg-white rounded-2xl border border-gray-100 hover:border-green-300 hover:shadow-lg hover:shadow-green-100/50 transition-all duration-200 p-5 flex flex-col items-center text-center overflow-hidden"
                            data-testid={`cat-card-${cat.slug}`}>
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center mb-3 group-hover:from-green-100 group-hover:to-green-200 transition-colors">
                                <Icon size={24} className="text-green-600" />
                            </div>
                            <h3 className="text-sm font-bold text-gray-800 group-hover:text-green-700 transition-colors mb-1">{cat.name}</h3>
                            {cat.product_count > 0 && (
                                <span className="text-[10px] text-gray-400 font-medium">{cat.product_count} produktů</span>
                            )}
                            {cat.description && (
                                <p className="text-[10px] text-gray-400 mt-1 line-clamp-2">{cat.description}</p>
                            )}
                            <div className="absolute inset-0 rounded-2xl ring-2 ring-green-400 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}

export function Skeleton({ viewMode, count = 12 }) {
    return (
        <div className={viewMode === "grid" ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" : viewMode === "b2b" ? "space-y-0.5" : "space-y-3"}>
            {Array(count).fill(0).map((_, i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse"
                    style={{ animationDelay: `${i * 40}ms`, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                    {viewMode === "grid" ? (
                        <><div className="bg-gray-50" style={{ paddingBottom: "130%" }} />
                        <div className="p-4 space-y-2">
                            <div className="h-2.5 bg-gray-100 rounded w-3/4" />
                            <div className="h-3.5 bg-gray-100 rounded w-1/2" />
                        </div></>
                    ) : viewMode === "b2b" ? (
                        <div className="flex h-10 items-center gap-4 px-3"><div className="h-2.5 bg-gray-100 rounded w-16" /><div className="h-2.5 bg-gray-100 rounded w-1/3" /><div className="h-2.5 bg-gray-100 rounded w-16 ml-auto" /></div>
                    ) : (
                        <div className="flex h-28"><div className="w-28 bg-gray-100 flex-shrink-0" />
                        <div className="p-3 flex-1 space-y-2"><div className="h-3 bg-gray-100 rounded w-2/3" /><div className="h-3 bg-gray-100 rounded w-1/3" /></div></div>
                    )}
                </div>
            ))}
        </div>
    );
}
